// @ts-nocheck
/**
 * Ludipek B2B favorites (wishlist).
 *
 * Wires every `[data-favorite-toggle][data-netsuite-id]` button on the page to the API toggle
 * endpoint. On click: optimistically flips the visible state, POSTs to
 * /customers/{shopify_customer_id}/favorites/toggle, and on failure reverts the icon back to
 * its prior state with a console warning (no toast — favorites is a silent affordance).
 *
 * The current list lives on window.__LUDIPEK_B2B__.favorites (an Array<{ns, h}>), painted SSR
 * from the customer.metafields.custom.favorites JSON metafield so hearts hydrate in the right
 * state on first frame without a fetch round-trip. Every successful toggle replaces this array
 * with the API response so duplicate hearts on the page (cards + PDP) all read the same source.
 *
 * Logged-out / pending buyers see no hearts (the SSR Liquid hides them); a defensive guard
 * here also short-circuits if shopifyCustomerId is missing.
 *
 * Exposes window.LudipekB2BFavorites = { isFavorite, toggle }.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  const TOGGLE_SELECTOR = '[data-favorite-toggle][data-netsuite-id]';
  const ACTIVE_CLASS = 'is-favorite';
  const FETCH_TIMEOUT_MS = 10_000;

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-favorites]', message);
    } else {
      console.debug('[b2b-favorites]', message, details);
    }
  }

  function logWarn(message, details) {
    if (details === undefined) {
      console.warn('[b2b-favorites]', message);
    } else {
      console.warn('[b2b-favorites]', message, details);
    }
  }

  function bootstrapIsUsable() {
    if (BOOTSTRAP === null) return false;
    if (!BOOTSTRAP.apiBaseUrl) return false;
    if (!BOOTSTRAP.shopifyCustomerId) return false;
    return true;
  }

  function currentFavorites() {
    if (!Array.isArray(BOOTSTRAP.favorites)) BOOTSTRAP.favorites = [];
    return BOOTSTRAP.favorites;
  }

  function isFavorite(netsuiteId) {
    const id = Number(netsuiteId);
    if (!Number.isFinite(id)) return false;
    return currentFavorites().some((entry) => Number(entry && entry.ns) === id);
  }

  // Update every visible heart for this netsuite id. Called both optimistically (before the
  // fetch) and after the fetch resolves, so the rendered state always reflects the bootstrap
  // array.
  function syncIconsForNetsuiteId(netsuiteId) {
    const id = Number(netsuiteId);
    if (!Number.isFinite(id)) return;
    const active = isFavorite(id);
    document.querySelectorAll(`${TOGGLE_SELECTOR}`).forEach((button) => {
      if (Number(button.getAttribute('data-netsuite-id')) !== id) return;
      button.classList.toggle(ACTIVE_CLASS, active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function syncAllIcons() {
    document.querySelectorAll(TOGGLE_SELECTOR).forEach((button) => {
      const id = Number(button.getAttribute('data-netsuite-id'));
      if (!Number.isFinite(id)) return;
      const active = isFavorite(id);
      button.classList.toggle(ACTIVE_CLASS, active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  async function postToggle(netsuiteId, handle) {
    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/customers/${BOOTSTRAP.shopifyCustomerId}/favorites/toggle`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-api-key': BOOTSTRAP.apiShopifyKey,
        },
        body: JSON.stringify({ netsuite_id: netsuiteId, handle }),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function toggle(netsuiteId, handle) {
    if (!bootstrapIsUsable()) return;
    const id = Number(netsuiteId);
    if (!Number.isFinite(id) || id <= 0) {
      logWarn('toggle called with non-numeric netsuiteId', { netsuiteId });
      return;
    }
    if (!handle) {
      logWarn('toggle called without handle — favorites page would not be able to render', { netsuiteId: id });
      return;
    }

    // Optimistic: mutate the bootstrap array and repaint hearts before the fetch resolves.
    const previousSnapshot = currentFavorites().slice();
    const exists = isFavorite(id);
    BOOTSTRAP.favorites = exists
      ? previousSnapshot.filter((entry) => Number(entry && entry.ns) !== id)
      : [...previousSnapshot, { ns: id, h: String(handle) }];
    syncIconsForNetsuiteId(id);

    try {
      const payload = await postToggle(id, String(handle));
      // Replace optimistic snapshot with the authoritative server list (covers races where
      // the same buyer toggled from another device between our SSR paint and this fetch).
      if (payload && Array.isArray(payload.favorites)) {
        BOOTSTRAP.favorites = payload.favorites.map((entry) => ({
          ns: Number(entry.netsuite_id),
          h: String(entry.handle),
        }));
        syncAllIcons();
      }
      logDebug(`toggled favorite ${id} (handle=${handle})`, { newCount: BOOTSTRAP.favorites.length });
    } catch (error) {
      logWarn('toggle failed — reverting', { netsuiteId: id, error: String(error) });
      BOOTSTRAP.favorites = previousSnapshot;
      syncIconsForNetsuiteId(id);
    }
  }

  // Click delegation on the document — so lazy-loaded cards (paginated grid, recommendations)
  // pick up the handler without any per-card wiring. The Liquid template controls which buttons
  // get [data-favorite-toggle][data-netsuite-id]; we just route the click.
  document.addEventListener('click', (event) => {
    const target = event.target && event.target.closest ? event.target.closest(TOGGLE_SELECTOR) : null;
    if (!target) return;
    event.preventDefault();
    const id = target.getAttribute('data-netsuite-id');
    const handle = target.getAttribute('data-favorite-handle');
    toggle(id, handle);
  });

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(syncAllIcons);

  window.LudipekB2BFavorites = { isFavorite, toggle };
})();
